import React, { useState, useEffect } from "react";
import { ref, update, get, onValue } from "firebase/database";
import { db } from "../services/firebase";
import { auth } from "../services/firebase";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "./StudentIDGenerator.css";

export default function StudentIDGenerator() {
  const [mode, setMode] = useState("college");
  const [institute, setInstitute] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [testType, setTestType] = useState("DEFAULT");
  const [selectedTestId, setSelectedTestId] = useState("DEFAULT"); // Link to specific test
  const [count, setCount] = useState(10);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [generated, setGenerated] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableTests, setAvailableTests] = useState([]); // Teacher's uploaded tests

  // Load teacher's tests
  useEffect(() => {
    if (!auth.currentUser) return;

    const loadTests = async () => {
      const testMap = new Map(); // Use Map to group by quizId
      
      // Load MCQ tests
      const mcqRef = ref(db, "quizzes");
      const mcqSnap = await get(mcqRef);
      if (mcqSnap.exists()) {
        Object.entries(mcqSnap.val()).forEach(([id, quiz]) => {
          if (quiz.createdBy === auth.currentUser.uid) {
            if (!testMap.has(id)) {
              testMap.set(id, {
                id,
                title: quiz.title,
                types: [],
                quizTypes: []
              });
            }
            testMap.get(id).types.push("mcq");
            testMap.get(id).quizTypes.push("mcq");
          }
        });
      }

      // Load Code Snippet tests
      const codeSnippetRef = ref(db, "codeSnippetQuestions");
      const codeSnippetSnap = await get(codeSnippetRef);
      if (codeSnippetSnap.exists()) {
        Object.entries(codeSnippetSnap.val()).forEach(([id, quiz]) => {
          if (quiz.createdBy === auth.currentUser.uid) {
            if (!testMap.has(id)) {
              testMap.set(id, {
                id,
                title: quiz.title,
                types: [],
                quizTypes: []
              });
            }
            testMap.get(id).types.push("codeSnippet");
            testMap.get(id).quizTypes.push("codeSnippet");
          }
        });
      }

      // Load Coding tests
      const codingRef = ref(db, "codingQuestions");
      const codingSnap = await get(codingRef);
      if (codingSnap.exists()) {
        Object.entries(codingSnap.val()).forEach(([id, quiz]) => {
          if (quiz.createdBy === auth.currentUser.uid || quiz.createdBy === auth.currentUser.email) {
            if (!testMap.has(id)) {
              testMap.set(id, {
                id,
                title: quiz.quizTitle || quiz.title,
                types: [],
                quizTypes: []
              });
            }
            testMap.get(id).types.push("coding");
            testMap.get(id).quizTypes.push("coding");
          }
        });
      }

      // Convert Map to Array - tests with same quizId are now combined
      const tests = Array.from(testMap.values());
      setAvailableTests(tests);
    };

    loadTests();
  }, []);

  /* ---------- STRONG PASSWORD (MAX 8 CHAR) ---------- */
  const passwordGen = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const number = "0123456789";
    const special = "@#$_-!%&*";
    const all = upper + lower + number + special;

    const pick = (s) => s[Math.floor(Math.random() * s.length)];

    // mandatory 4
    let pwd =
      pick(upper) +
      pick(lower) +
      pick(number) +
      pick(special);

    // remaining to make exactly 8
    for (let i = 0; i < 4; i++) {
      pwd += pick(all);
    }

    return pwd
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");
  };

  /* ---------- MAIN LOGIC ---------- */
  const generateIDs = async () => {
    if (!institute || !startTime || !endTime) {
      alert("Please fill all required fields");
      return;
    }

    if (new Date(endTime) <= new Date(startTime)) {
      alert("End time must be after start time");
      return;
    }

    setLoading(true);

    // Generate 5-digit random number (XXXXX)
    const random5Digit = Math.floor(10000 + Math.random() * 90000);
    
    // Format: COLLEGE-YEAR-XXXXX_TESTTYPE-N
    // Example: VIT-2025-48392-NORMALTEST-1
    const cleanInstitute = institute.replace(/[^A-Za-z]/g, "").toUpperCase();
    const cleanTestType = testType.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    
    const updates = {};
    const preview = [];

    for (let i = 1; i <= Number(count); i++) {
      // Format: COLLEGE-YEAR-XXXXX_TESTTYPE-N
      const id = `${cleanInstitute}-${year}-${random5Digit}_${cleanTestType}-${i}`;
      const password = passwordGen();

      // Determine testId - use selectedTestId (DEFAULT or teacher's test)
      const testId = selectedTestId === "DEFAULT" ? "DEFAULT" : selectedTestId;

      updates[`studentCredentials/${id}`] = {
        password,
        testId: testId, // Link to DEFAULT or teacher's test
        testType: cleanTestType,
        institute: cleanInstitute,
        year,
        mode,
        startTime,
        endTime,
        expiresAt: new Date(endTime).getTime(), // Timestamp for expiry check
        status: "ACTIVE",
        used: false,
        createdAt: Date.now(),
        session: {
          active: false,
          lastLogin: null,
          device: null,
          ip: null,
        },
        student: {
          name: "",
          roll: "",
          uploaded: false,
        },
      };

      preview.push({ id, password });
    }

    await update(ref(db), updates);
    setGenerated(preview);
    alert(`✅ Generated ${count} student IDs successfully!`);
    setLoading(false);
  };

  /* ---------- CSV DOWNLOAD ---------- */
  const downloadCSV = () => {
    let csv = "ID,Password\n";
    generated.forEach((g) => {
      csv += `${g.id},${g.password}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "student_ids.csv";
    link.click();
  };

  /* ---------- PDF DOWNLOAD ---------- */
  const downloadPDF = () => {
  const doc = new jsPDF();

  doc.setFontSize(14);
  doc.text("Student Test Credentials", 14, 15);

  doc.setFontSize(10);
  doc.text(`Institute: ${institute}`, 14, 22);
  doc.text(`Test Type: ${testType}`, 14, 28);
  doc.text(`Valid: ${startTime} to ${endTime}`, 14, 34);

  const tableData = generated.map((g, i) => [
    i + 1,
    g.id,
    g.password,
  ]);

  // ✅ CORRECT WAY
  autoTable(doc, {
    startY: 40,
    head: [["#", "Student ID", "Password"]],
    body: tableData,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [99, 102, 241] },
  });

  doc.save("student_ids.pdf");
};


  /* ---------- UI ---------- */
  return (
    <div className="sid-wrapper">
      <h2 className="sid-title">🎓 Student ID Generator</h2>

      <div className="sid-card">
        <div className="sid-row">
          <label>Institute Type</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="college">College / University</option>
            <option value="school">School</option>
          </select>
        </div>

        <div className="sid-row">
          <label>Institute Short Name</label>
          <input
            placeholder="VIT / DAV / IIT"
            value={institute}
            onChange={(e) => setInstitute(e.target.value.toUpperCase())}
          />
        </div>

        <div className="sid-row">
          <label>Academic Year</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </div>

        <div className="sid-row">
          <label>Test Type (for ID format)</label>
          <select value={testType} onChange={(e) => setTestType(e.target.value)}>
            <option value="DEFAULT">DEFAULT</option>
            <option value="NORMALTEST">NORMALTEST</option>
            <option value="MOCKTEST">MOCKTEST</option>
            <option value="PRACTICETEST">PRACTICETEST</option>
            <option value="FINALTEST">FINALTEST</option>
            <option value="CUSTOM">Custom (Enter below)</option>
          </select>
          {testType === "CUSTOM" && (
            <input
              placeholder="Enter custom test type"
              onChange={(e) => setTestType(e.target.value.toUpperCase())}
              style={{ marginTop: "0.5rem" }}
            />
          )}
        </div>

        <div className="sid-row">
          <label>Link to Test (Which test will students take?)</label>
          <select 
            value={selectedTestId} 
            onChange={(e) => setSelectedTestId(e.target.value)}
          >
            <option value="DEFAULT">DEFAULT (Global Test)</option>
            {availableTests.map((test) => {
              // Show quiz types in a readable format
              const typesLabel = test.types.length > 0 
                ? test.types.map(t => {
                    if (t === "mcq") return "MCQ";
                    if (t === "codeSnippet") return "Code Snippet";
                    if (t === "coding") return "Coding";
                    return t;
                  }).join(" + ")
                : "Test";
              
              return (
                <option key={test.id} value={test.id}>
                  {test.title} {test.types.length > 1 ? `(${typesLabel})` : ""}
                </option>
              );
            })}
          </select>
          <small style={{ color: "#666", marginTop: "0.25rem", display: "block" }}>
            {selectedTestId === "DEFAULT" 
              ? "Students will take the global default test" 
              : (() => {
                  const selectedTest = availableTests.find(t => t.id === selectedTestId);
                  if (selectedTest && selectedTest.types.length > 1) {
                    return `Students will take combined test: ${selectedTest.types.map(t => {
                      if (t === "mcq") return "MCQ";
                      if (t === "codeSnippet") return "Code Snippet";
                      if (t === "coding") return "Coding";
                      return t;
                    }).join(" + ")}`;
                  }
                  return "Students will take your uploaded test";
                })()}
          </small>
        </div>

        <div className="sid-row">
          <label>Number of Students</label>
          <input
            type="number"
            min="1"
            max="500"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          />
        </div>

        <div className="sid-row">
          <label>Start Time</label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>

        <div className="sid-row">
          <label>End Time</label>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>

        <button className="sid-btn" onClick={generateIDs} disabled={loading}>
          {loading ? "Generating..." : "🚀 Generate IDs"}
        </button>
      </div>

      {generated.length > 0 && (
        <div className="sid-preview">
          <h3>Generated IDs</h3>

          <table className="sid-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Password</th>
              </tr>
            </thead>
            <tbody>
              {generated.map((g, i) => (
                <tr key={i}>
                  <td>{g.id}</td>
                  <td>{g.password}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sid-actions">
            <button className="sid-download" onClick={downloadCSV}>
              ⬇ CSV
            </button>
            <button className="sid-download sid-pdf" onClick={downloadPDF}>
              📄 PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
